import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import { navigate } from "../../../redux/slice/layoutSlice";
import { clearSelected } from "../../../redux/slice/selectionSlice";
import {
  useGetUserQuery,
  useUpdateUserMutation,
  useSignupMutation,
  useGetPlansQuery,
} from "../../../redux/api";
import { validateEmail, validatePhone } from "../../../utils/validation";
import { PhoneInput } from "../../../components/PhoneInput";
import { Field } from "../../../components/Field";

const ENTITY = "owner";
const LIST_PATH = "/owners";

type OwnerForm = {
  prefix: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  status: string;
  organizationName: string;
  planId: string;
};

const emptyForm: OwnerForm = {
  prefix: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  status: "active",
  organizationName: "",
  planId: "",
};

// Platform Owner → Owner. Creating an owner provisions a brand-new organization
// for them (an org has exactly one owner): it runs the signup flow, which makes
// the org, the owner user, and a pending subscription. Editing updates the user.
const Owner = () => {
  const dispatch = useAppDispatch();
  const selectedId = useAppSelector((s) => s.selection[ENTITY]);
  const isEdit = !!selectedId;

  const { data: existing } = useGetUserQuery(selectedId ?? "", {
    skip: !selectedId,
  });
  const { data: plans = [] } = useGetPlansQuery();

  const [signup, { isLoading: signingUp }] = useSignupMutation();
  const [updateUser, { isLoading: updating }] = useUpdateUserMutation();

  const [form, setForm] = useState<OwnerForm>(emptyForm);
  const [baseline, setBaseline] = useState<OwnerForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  const saving = signingUp || updating;

  useEffect(() => {
    if (!existing) return;
    const next: OwnerForm = {
      ...emptyForm,
      prefix: existing.prefix ?? "",
      firstName: existing.firstName ?? "",
      lastName: existing.lastName ?? "",
      email: existing.email ?? "",
      phone: existing.phone ?? "",
      status: existing.status ?? "active",
    };
    setForm(next);
    setBaseline(next);
    setFieldErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, existing?.updatedAt]);

  const setField = (key: keyof OwnerForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Generate a strong password the platform owner can copy and send to the new
  // owner (ambiguous characters like O/0, l/1 are left out on purpose).
  const handleGeneratePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
    let pwd = "";
    for (let i = 0; i < 14; i++) {
      pwd += chars[Math.floor(Math.random() * chars.length)];
    }
    setField("password", pwd);
    setShowPassword(true);
    setCopied(false);
  };

  const handleCopyPassword = async () => {
    if (!form.password) return;
    try {
      await navigator.clipboard.writeText(form.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — the password is visible to copy manually.
    }
  };

  const goBack = () => {
    dispatch(clearSelected(ENTITY));
    dispatch(navigate(LIST_PATH));
  };

  const handleSave = async () => {
    setFormError(null);

    const errors: Record<string, string> = {};
    if (!form.firstName.trim()) errors.firstName = "First name is required.";
    if (!form.lastName.trim()) errors.lastName = "Last name is required.";

    const phoneError = validatePhone(form.phone);
    if (phoneError) errors.phone = phoneError;

    if (!isEdit) {
      const emailError = validateEmail(form.email);
      if (emailError) errors.email = emailError;
      if (form.password.length < 6) {
        errors.password = "Password must be at least 6 characters.";
      }
      if (!form.organizationName.trim()) {
        errors.organizationName = "Organization name is required.";
      }
      if (!form.planId) errors.planId = "Select a plan.";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    try {
      if (isEdit && selectedId) {
        await updateUser({
          id: selectedId,
          input: {
            prefix: form.prefix.trim() || undefined,
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            phone: form.phone.trim() || undefined,
            status: form.status,
          },
        }).unwrap();
      } else {
        // Creates the organization + owner user + pending subscription.
        const tokens = await signup({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          password: form.password,
          organizationName: form.organizationName.trim(),
          planId: form.planId,
          // active here provisions an active org + paid subscription (no pending
          // approval); other statuses create a pending trial.
          status: form.status,
        }).unwrap();

        // Apply the extra owner fields the signup flow doesn't take.
        const hasExtras =
          !!form.prefix.trim() || !!form.phone.trim() || form.status !== "active";
        if (tokens.userId && hasExtras) {
          await updateUser({
            id: tokens.userId,
            input: {
              prefix: form.prefix.trim() || undefined,
              phone: form.phone.trim() || undefined,
              status: form.status,
            },
          }).unwrap();
        }
      }
      goBack();
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to save owner.",
      );
    }
  };

  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  return (
    <div className="dashboard">
      <div className="dashboard__head">
        <div>
          <h1>{isEdit ? "Edit owner" : "New owner"}</h1>
          <p>
            {isEdit
              ? "Owner / user account."
              : "Creates the owner and a new organization for them."}
          </p>
        </div>
        <button type="button" className="btn" onClick={goBack}>
          ← Back to owners
        </button>
      </div>

      <section className="panel">
        {formError ? <div className="form-error">{formError}</div> : null}

        <div className="form-grid">
          <label className="field">
            Prefix
            <input
              value={form.prefix}
              onChange={(e) => setField("prefix", e.target.value)}
              placeholder="Mr / Ms"
            />
          </label>
          <label className="field">
            Status
            <select
              value={form.status}
              onChange={(e) => setField("status", e.target.value)}
            >
              <option value="active">Active</option>
              <option value="invited">Invited</option>
              <option value="pending">Pending</option>
              <option value="disabled">Disabled</option>
              <option value="deleted">Deleted</option>
            </select>
          </label>
          <Field label="First name *" error={fieldErrors.firstName}>
            <input
              value={form.firstName}
              onChange={(e) => {
                setField("firstName", e.target.value);
                setFieldErrors((prev) => ({ ...prev, firstName: undefined }));
              }}
            />
          </Field>
          <Field label="Last name *" error={fieldErrors.lastName}>
            <input
              value={form.lastName}
              onChange={(e) => {
                setField("lastName", e.target.value);
                setFieldErrors((prev) => ({ ...prev, lastName: undefined }));
              }}
            />
          </Field>
          <Field label={<>Email {isEdit ? "" : "*"}</>} error={fieldErrors.email}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => {
                setField("email", e.target.value);
                setFieldErrors((prev) => ({ ...prev, email: undefined }));
              }}
              disabled={isEdit}
              title={isEdit ? "Email can't be changed." : undefined}
            />
          </Field>
          <Field label="Phone" error={fieldErrors.phone}>
            <PhoneInput
              value={form.phone}
              onChange={(v) => {
                setField("phone", v);
                setFieldErrors((prev) => ({ ...prev, phone: undefined }));
              }}
            />
          </Field>

          {isEdit ? null : (
            <>
              <Field label="Password *" error={fieldErrors.password}>
                <div style={{ display: "flex", gap: "0.4rem" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => {
                      setField("password", e.target.value);
                      setFieldErrors((prev) => ({ ...prev, password: undefined }));
                    }}
                    autoComplete="new-password"
                    placeholder="At least 6 characters"
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowPassword((s) => !s)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={handleGeneratePassword}
                  >
                    Generate
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={handleCopyPassword}
                    disabled={!form.password}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-faint)",
                    fontWeight: 400,
                  }}
                >
                  Share this password with the owner — they can change it after
                  first login.
                </span>
              </Field>
              <Field label="Organization name *" error={fieldErrors.organizationName}>
                <input
                  value={form.organizationName}
                  onChange={(e) => {
                    setField("organizationName", e.target.value);
                    setFieldErrors((prev) => ({ ...prev, organizationName: undefined }));
                  }}
                  placeholder="Acme Inc."
                />
              </Field>
              <Field label="Plan *" error={fieldErrors.planId}>
                <select
                  value={form.planId}
                  onChange={(e) => {
                    setField("planId", e.target.value);
                    setFieldErrors((prev) => ({ ...prev, planId: undefined }));
                  }}
                >
                  <option value="">— Select a plan —</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </Field>
            </>
          )}
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn"
            onClick={goBack}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create owner"}
          </button>
        </div>
      </section>
    </div>
  );
};

export default Owner;
